import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, TextInput } from 'react-native-web';
import blueLandscapeIcon from '../icons/blue-landscape-icon.png';
import downArrowIcon from "../icons/DupeIcons-03-Arrow.png";
import Button from './Button';
import { createCreatorReview, getCreatorReviews } from '../services/BusinessService';
import toast from 'react-hot-toast';

const CreatorReviewForm = ({ creator, showCreatorInfo = true, campaignId, businessId }) => {
    const [responsiveness, setResponsiveness] = useState(0);
    const [quality, setQuality] = useState(0);
    const [comments, setComments] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [existingReview, setExistingReview] = useState(null);
    const [isLoadingReview, setIsLoadingReview] = useState(true);

    const canSubmit = responsiveness > 0 && quality > 0 && !isSubmitting && !existingReview;
    const isReadOnly = !!existingReview;

    useEffect(() => {
        fetchExistingReview();
    }, [creator.id, campaignId, businessId]);

    const fetchExistingReview = async () => {
        if (!creator.id || !campaignId || !businessId) {
            setIsLoadingReview(false);
            return;
        }

        try {
            const response = await getCreatorReviews(creator.id);
            if (response && response.status === 200 && response.data) {
                // Find the review for this specific business/campaign combination
                const review = response.data.find(r => 
                    r.business_id === businessId && r.campaign_id === campaignId
                );
                
                if (review) {
                    setExistingReview(review);
                    setResponsiveness(review.responsiveness_rating);
                    setQuality(review.quality_rating);
                    setComments(review.comment || '');
                }
            }
        } catch (error) {
            console.error('Error fetching existing reviews:', error);
        } finally {
            setIsLoadingReview(false);
        }
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;

        setIsSubmitting(true);
        
        const reviewData = {
            business_id: businessId,
            creator_id: creator.id,
            campaign_id: campaignId,
            quality_rating: quality,
            responsiveness_rating: responsiveness,
            comment: comments || ''
        };

        try {
            const response = await createCreatorReview(creator.id, reviewData);
            if (response && response.status === 200) {
                toast.success('Review submitted successfully!');
                // Refresh to show the new review as read-only
                await fetchExistingReview();
            } else {
                toast.error('Failed to submit review. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting review:', error);
            toast.error('Failed to submit review. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStars = (rating, setRating) => (
        <View style={styles.starsContainer}>
            {[1, 2, 3, 4, 5].map((star) => (
                <TouchableOpacity
                    key={star}
                    onPress={() => !isReadOnly && setRating(star)}
                    style={[styles.starButton, isReadOnly && styles.starButtonDisabled]}
                    disabled={isSubmitting || isReadOnly}
                >
                    <Text style={[
                        styles.star,
                        star <= rating ? styles.starFilled : styles.starEmpty
                    ]}>★</Text>
                </TouchableOpacity>
            ))}
        </View>
    );

    const renderReviewForm = () => {
        if (isLoadingReview) {
            return (
                <View style={[showCreatorInfo ? styles.reviewForm : styles.reviewFormExpanded, styles.loadingContainer]}>
                    <Text style={styles.loadingText}>Loading existing review...</Text>
                </View>
            );
        }

        return (
            <View style={showCreatorInfo ? styles.reviewForm : styles.reviewFormExpanded}>
                <View style={styles.ratingsRow}>
                    <View style={styles.ratingSectionRow}>
                        <Text style={[styles.ratingLabel, isReadOnly && styles.ratingLabelReadOnly]}>
                            Responsiveness {!isReadOnly && '*'}
                        </Text>
                        {renderStars(responsiveness, setResponsiveness)}
                    </View>
                    <View style={styles.ratingSectionRow}>
                        <Text style={[styles.ratingLabel, isReadOnly && styles.ratingLabelReadOnly]}>
                            Quality {!isReadOnly && '*'}
                        </Text>
                        {renderStars(quality, setQuality)}
                    </View>
                </View>
                <View style={styles.commentsSection}>
                    <Text style={[styles.ratingLabel, isReadOnly && styles.ratingLabelReadOnly]}>Comments</Text>
                    {isReadOnly ? (
                        <View style={styles.commentsDisplayContainer}>
                            <Text style={styles.commentsDisplayText}>
                                {comments || "No comments provided"}
                            </Text>
                        </View>
                    ) : (
                        <TextInput
                            style={styles.commentsInput}
                            multiline
                            numberOfLines={4}
                            value={comments}
                            onChangeText={setComments}
                            placeholder="Add your feedback here..."
                            editable={!isSubmitting}
                        />
                    )}
                </View>
                {!isReadOnly && (
                    <View style={styles.submitButtonContainer}>
                        <Button 
                            text={isSubmitting ? "Submitting..." : "Submit"}
                            onPress={handleSubmit}
                            style={[
                                styles.submitButton,
                                !canSubmit && styles.submitButtonDisabled
                            ]}
                            disabled={!canSubmit}
                        />
                    </View>
                )}
            </View>
        );
    };

    if (!showCreatorInfo) {
        // Campaign page mode - just show the expanded form without creator info
        return (
            <View style={styles.creatorReviewContainer}>
                {renderReviewForm()}
            </View>
        );
    }

    // Reviews page mode - show creator info with expand/collapse functionality
    return (
        <View style={styles.creatorReviewContainer}>
            <TouchableOpacity 
                style={styles.creatorInfoContainer}
                onPress={() => setIsExpanded(!isExpanded)}
            >
                <View style={styles.creatorInfo}>
                    <Image 
                        style={styles.creatorImage} 
                        source={creator.profile_image_exists 
                            ? {uri: 'https://d3p3fw3rutb1if.cloudfront.net/users-preview/' + creator.id}
                            : blueLandscapeIcon
                        }
                        defaultSource={blueLandscapeIcon}
                    />
                    <Text style={styles.creatorName}>
                        {creator.first_name} {creator.last_name}
                        {isReadOnly && <Text style={styles.reviewedIndicator}> ✓ Reviewed</Text>}
                    </Text>
                </View>
                <Image 
                    source={downArrowIcon} 
                    style={[
                        styles.expandIcon,
                        isExpanded && styles.expandIconRotated
                    ]} 
                />
            </TouchableOpacity>
            {isExpanded && renderReviewForm()}
        </View>
    );
};

const styles = StyleSheet.create({
    creatorReviewContainer: {
        backgroundColor: 'white',
        borderRadius: 10,
        padding: 15,
        marginBottom: 15,
        borderWidth: 1,
        borderColor: '#e0e0e0',
    },
    creatorInfoContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
    },
    creatorInfo: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    creatorImage: {
        width: 40,
        height: 40,
        borderRadius: 20,
        marginRight: 10,
    },
    creatorName: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#737373',
        fontFamily: "'lato', sans-serif",
    },
    reviewedIndicator: {
        color: '#4CAF50',
        fontSize: 14,
        fontWeight: 'normal',
    },
    reviewForm: {
        marginTop: 15,
        paddingTop: 15,
        borderTopWidth: 1,
        borderTopColor: '#e0e0e0',
    },
    reviewFormExpanded: {
        // No top margin, padding, or border for campaign page mode
    },
    loadingContainer: {
        alignItems: 'center',
        padding: 20,
    },
    loadingText: {
        color: '#737373',
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        fontStyle: 'italic',
    },
    ratingsRow: {
        flexDirection: 'row',
        marginBottom: 15,
    },
    ratingSection: {
        marginBottom: 15,
    },
    ratingSectionRow: {
        marginRight: 40,
    },
    ratingLabel: {
        fontSize: 14,
        color: '#737373',
        fontFamily: "'lato', sans-serif",
        marginBottom: 5,
    },
    ratingLabelReadOnly: {
        color: '#999999',
    },
    starsContainer: {
        flexDirection: 'row',
    },
    starButton: {
        padding: 5,
    },
    starButtonDisabled: {
        cursor: 'default',
    },
    star: {
        fontSize: 24,
    },
    starFilled: {
        color: '#FFD700',
    },
    starEmpty: {
        color: '#e0e0e0',
    },
    commentsSection: {
        marginTop: 10,
    },
    commentsInput: {
        borderWidth: 1,
        borderColor: '#e0e0e0',
        borderRadius: 5,
        padding: 10,
        minHeight: 100,
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
    },
    commentsDisplayContainer: {
        padding: 10,
        minHeight: 100,
    },
    commentsDisplayText: {
        fontFamily: "'lato', sans-serif",
        fontSize: 14,
        color: '#666666',
        lineHeight: 20,
    },
    submitButtonContainer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 15,
    },
    submitButton: {
        width: 120,
    },
    submitButtonDisabled: {
        opacity: 0.5,
    },
    expandIcon: {
        height: 20,
        width: 20,
    },
    expandIconRotated: {
        transform: [{ rotate: '180deg' }],
    },
});

export default CreatorReviewForm; 